
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {NewsProfileModifyResponse} from './NewsProfileModifyResponse';

/**
 * NewsProfileModifyRequest struct
 */
class NewsProfileModifyRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} newsProfileId
   * @param {string=} newsProfileName
   * @param {string=} newName
   * @param {string=} newComment
   * @param {string=} newDescription
   * @param {Date=} newTime
   * @param {number=} newBeforeSeconds
   * @param {number=} newAfterSeconds
   * @param {UUID=} newMarginRateProfileId
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), newsProfileId = null, newsProfileName = null, newName = null, newComment = null, newDescription = null, newTime = null, newBeforeSeconds = null, newAfterSeconds = null, newMarginRateProfileId = null) {
    super()
    super.copy(parent)
    this.NewsProfileId = newsProfileId
    this.NewsProfileName = newsProfileName
    this.NewName = newName
    this.NewComment = newComment
    this.NewDescription = newDescription
    this.NewTime = newTime
    this.NewBeforeSeconds = newBeforeSeconds
    this.NewAfterSeconds = newAfterSeconds
    this.NewMarginRateProfileId = newMarginRateProfileId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!NewsProfileModifyRequest}
   * @param {!NewsProfileModifyRequest} other Other struct
   * @returns {!NewsProfileModifyRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.NewsProfileId != null) {
      this.NewsProfileId = new UUID(other.NewsProfileId)
    } else {
      this.NewsProfileId = null
    }
    if (other.NewsProfileName != null) {
      this.NewsProfileName = other.NewsProfileName
    } else {
      this.NewsProfileName = null
    }
    if (other.NewName != null) {
      this.NewName = other.NewName
    } else {
      this.NewName = null
    }
    if (other.NewComment != null) {
      this.NewComment = other.NewComment
    } else {
      this.NewComment = null
    }
    if (other.NewDescription != null) {
      this.NewDescription = other.NewDescription
    } else {
      this.NewDescription = null
    }
    if (other.NewTime != null) {
      if (other.NewTime instanceof Date) {
        this.NewTime = new Date(other.NewTime.getTime())
      } else {
        this.NewTime = new Date(Math.round(other.NewTime / 1000000))
      }
    } else {
      this.NewTime = null
    }
    if (other.NewBeforeSeconds != null) {
      this.NewBeforeSeconds = UInt64.fromNumber(other.NewBeforeSeconds)
    } else {
      this.NewBeforeSeconds = null
    }
    if (other.NewAfterSeconds != null) {
      this.NewAfterSeconds = UInt64.fromNumber(other.NewAfterSeconds)
    } else {
      this.NewAfterSeconds = null
    }
    if (other.NewMarginRateProfileId != null) {
      this.NewMarginRateProfileId = new UUID(other.NewMarginRateProfileId)
    } else {
      this.NewMarginRateProfileId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!NewsProfileModifyRequest}
   * @returns {!NewsProfileModifyRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new NewsProfileModifyRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new NewsProfileModifyRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!NewsProfileModifyRequest}
   * @param {!NewsProfileModifyRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof NewsProfileModifyRequest)) {
      throw new TypeError('Instance of NewsProfileModifyRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!NewsProfileModifyRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      NewsProfileId: ((this.NewsProfileId != null) ? this.NewsProfileId.toString() : null),
      NewsProfileName: ((this.NewsProfileName != null) ? this.NewsProfileName : null),
      NewName: ((this.NewName != null) ? this.NewName : null),
      NewComment: ((this.NewComment != null) ? this.NewComment : null),
      NewDescription: ((this.NewDescription != null) ? this.NewDescription : null),
      NewTime: ((this.NewTime != null) ? (this.NewTime.getTime() * 1000000) : null),
      NewBeforeSeconds: ((this.NewBeforeSeconds != null) ? this.NewBeforeSeconds.toNumber() : null),
      NewAfterSeconds: ((this.NewAfterSeconds != null) ? this.NewAfterSeconds.toNumber() : null),
      NewMarginRateProfileId: ((this.NewMarginRateProfileId != null) ? this.NewMarginRateProfileId.toString() : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return NewsProfileModifyRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!NewsProfileModifyRequest} other Object value
   * @returns {!NewsProfileModifyRequest} Created struct
   */
  static fromObject (other) {
    return new NewsProfileModifyRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!NewsProfileModifyRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return NewsProfileModifyRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!NewsProfileModifyRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6401
  }
}

export { NewsProfileModifyRequest };

/**
 * Fast Binary Encoding NewsProfileModifyRequest field model
 */
class FieldModelNewsProfileModifyRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.NewsProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.NewsProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewsProfileId.fbeOffset + this.NewsProfileId.fbeSize), buffer, this.NewsProfileId.fbeOffset + this.NewsProfileId.fbeSize)
    this.NewName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewsProfileName.fbeOffset + this.NewsProfileName.fbeSize), buffer, this.NewsProfileName.fbeOffset + this.NewsProfileName.fbeSize)
    this.NewComment = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), buffer, this.NewName.fbeOffset + this.NewName.fbeSize)
    this.NewDescription = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize), buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize)
    this.NewTime = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize), buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize)
    this.NewBeforeSeconds = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.NewTime.fbeOffset + this.NewTime.fbeSize), buffer, this.NewTime.fbeOffset + this.NewTime.fbeSize)
    this.NewAfterSeconds = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.NewBeforeSeconds.fbeOffset + this.NewBeforeSeconds.fbeSize), buffer, this.NewBeforeSeconds.fbeOffset + this.NewBeforeSeconds.fbeSize)
    this.NewMarginRateProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this.NewAfterSeconds.fbeOffset + this.NewAfterSeconds.fbeSize), buffer, this.NewAfterSeconds.fbeOffset + this.NewAfterSeconds.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.NewsProfileId.fbeSize + this.NewsProfileName.fbeSize + this.NewName.fbeSize + this.NewComment.fbeSize + this.NewDescription.fbeSize + this.NewTime.fbeSize + this.NewBeforeSeconds.fbeSize + this.NewAfterSeconds.fbeSize + this.NewMarginRateProfileId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.NewsProfileId.fbeExtra + this.NewsProfileName.fbeExtra + this.NewName.fbeExtra + this.NewComment.fbeExtra + this.NewDescription.fbeExtra + this.NewTime.fbeExtra + this.NewBeforeSeconds.fbeExtra + this.NewAfterSeconds.fbeExtra + this.NewMarginRateProfileId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelNewsProfileModifyRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6401
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.NewsProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewsProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileId.fbeSize

    if ((fbeCurrentSize + this.NewsProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewsProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewComment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewTime.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewTime.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewTime.fbeSize

    if ((fbeCurrentSize + this.NewBeforeSeconds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewBeforeSeconds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewBeforeSeconds.fbeSize

    if ((fbeCurrentSize + this.NewAfterSeconds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewAfterSeconds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewAfterSeconds.fbeSize

    if ((fbeCurrentSize + this.NewMarginRateProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewMarginRateProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewMarginRateProfileId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!NewsProfileModifyRequest} fbeValue Default value, defaults is new NewsProfileModifyRequest()
   * @returns {!NewsProfileModifyRequest} NewsProfileModifyRequest value
   */
  get (fbeValue = new NewsProfileModifyRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!NewsProfileModifyRequest} fbeValue NewsProfileModifyRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.NewsProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.NewsProfileId = this.NewsProfileId.get()
    } else {
      fbeValue.NewsProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileId.fbeSize

    if ((fbeCurrentSize + this.NewsProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.NewsProfileName = this.NewsProfileName.get()
    } else {
      fbeValue.NewsProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewsProfileName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) <= fbeStructSize) {
      fbeValue.NewName = this.NewName.get()
    } else {
      fbeValue.NewName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) <= fbeStructSize) {
      fbeValue.NewComment = this.NewComment.get()
    } else {
      fbeValue.NewComment = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) <= fbeStructSize) {
      fbeValue.NewDescription = this.NewDescription.get()
    } else {
      fbeValue.NewDescription = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewTime.fbeSize) <= fbeStructSize) {
      fbeValue.NewTime = this.NewTime.get()
    } else {
      fbeValue.NewTime = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewTime.fbeSize

    if ((fbeCurrentSize + this.NewBeforeSeconds.fbeSize) <= fbeStructSize) {
      fbeValue.NewBeforeSeconds = this.NewBeforeSeconds.get()
    } else {
      fbeValue.NewBeforeSeconds = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewBeforeSeconds.fbeSize

    if ((fbeCurrentSize + this.NewAfterSeconds.fbeSize) <= fbeStructSize) {
      fbeValue.NewAfterSeconds = this.NewAfterSeconds.get()
    } else {
      fbeValue.NewAfterSeconds = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewAfterSeconds.fbeSize

    if ((fbeCurrentSize + this.NewMarginRateProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.NewMarginRateProfileId = this.NewMarginRateProfileId.get()
    } else {
      fbeValue.NewMarginRateProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewMarginRateProfileId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelNewsProfileModifyRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {!NewsProfileModifyRequest} fbeValue NewsProfileModifyRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelNewsProfileModifyRequest}
   * @param {NewsProfileModifyRequest} fbeValue NewsProfileModifyRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.NewsProfileId.set(fbeValue.NewsProfileId)
    this.NewsProfileName.set(fbeValue.NewsProfileName)
    this.NewName.set(fbeValue.NewName)
    this.NewComment.set(fbeValue.NewComment)
    this.NewDescription.set(fbeValue.NewDescription)
    this.NewTime.set(fbeValue.NewTime)
    this.NewBeforeSeconds.set(fbeValue.NewBeforeSeconds)
    this.NewAfterSeconds.set(fbeValue.NewAfterSeconds)
    this.NewMarginRateProfileId.set(fbeValue.NewMarginRateProfileId)
  }
}

export { FieldModelNewsProfileModifyRequest };

/**
 * Fast Binary Encoding NewsProfileModifyRequest model
 */
class NewsProfileModifyRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelNewsProfileModifyRequest(this.buffer, 4)
  }

  /**
   * Get the NewsProfileModifyRequest model
   * @this {!NewsProfileModifyRequestModel}
   * @returns {!FieldModelNewsProfileModifyRequest} model NewsProfileModifyRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!NewsProfileModifyRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!NewsProfileModifyRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return NewsProfileModifyRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!NewsProfileModifyRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelNewsProfileModifyRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!NewsProfileModifyRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!NewsProfileModifyRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!NewsProfileModifyRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!NewsProfileModifyRequestModel}
   * @param {!NewsProfileModifyRequest} value NewsProfileModifyRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!NewsProfileModifyRequestModel}
   * @param {!NewsProfileModifyRequest} value NewsProfileModifyRequest value, defaults is new NewsProfileModifyRequest()
   * @return {!object} Deserialized NewsProfileModifyRequest value and its size
   */
  deserialize (value = new NewsProfileModifyRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new NewsProfileModifyRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new NewsProfileModifyRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!NewsProfileModifyRequestModel}
   * @param {!number} prev Previous NewsProfileModifyRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { NewsProfileModifyRequestModel };
NewsProfileModifyRequest.__has_response = true;
NewsProfileModifyRequest.__response_class = NewsProfileModifyResponse;
